import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { Store } from '@ngrx/store';
import { MtmAuthBridgeService } from '@mtm/auth-ui-angular';
import { TranslatePipe } from '@ngx-translate/core';

import { UiActions, selectSidebarCollapsed } from '@stores/ui';
import { ConsoleContextService } from '@core/services/console-context.service';
import { IconComponent, IconName } from '@shared/icons/icon/icon.component';
import { ShellStateService } from '../shell/shell-state.service';
import { TenantSelectorComponent } from './tenant-selector/tenant-selector.component';
import { SubscriptionSelectorComponent } from './subscription-selector/subscription-selector.component';

interface NavItem {
  labelKey: string;
  icon: IconName;
  path: string;
}

const PLATFORM_NAV: NavItem[] = [
  { labelKey: 'SIDEBAR.NAV.TENANTS', icon: 'building', path: 'tenants' },
  { labelKey: 'SIDEBAR.NAV.ACCOUNTS_MANAGEMENT', icon: 'users', path: 'accounts' },
  { labelKey: 'SIDEBAR.NAV.GROUPS', icon: 'layers', path: 'groups' },
];

const CONSOLE_NAV: NavItem[] = [
  { labelKey: 'NAV.DASHBOARD', icon: 'dashboard', path: 'dashboard' },
  { labelKey: 'NAV.AGENTS', icon: 'bot', path: 'agents' },
  { labelKey: 'NAV.API_KEYS', icon: 'key', path: 'api-keys' },
  { labelKey: 'NAV.MONITORING', icon: 'activity', path: 'monitoring' },
  { labelKey: 'NAV.AUDIT_LOGS', icon: 'history', path: 'audit-logs' },
];

@Component({
  selector: 'mtm-app-sidebar',
  standalone: true,
  imports: [
    RouterLink,
    RouterLinkActive,
    IconComponent,
    TenantSelectorComponent,
    SubscriptionSelectorComponent,
    TranslatePipe,
  ],
  templateUrl: './app-sidebar.component.html',
  styleUrl: './app-sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppSidebarComponent {
  private readonly store = inject(Store);
  private readonly bridge = inject(MtmAuthBridgeService);
  readonly context = inject(ConsoleContextService);
  readonly shell = inject(ShellStateService);

  readonly platformNav = PLATFORM_NAV;
  readonly consoleNav = CONSOLE_NAV;

  private readonly collapsedState = this.store.selectSignal(selectSidebarCollapsed);
  // The mobile drawer always shows labels, whatever the desktop collapse state.
  readonly collapsed = computed(() => this.collapsedState() && !this.shell.mobileNavOpen());

  // Base segments for AI console links, sourced from the selected context
  readonly consoleBase = computed<string[] | null>(() => {
    const tenant = this.context.selectedTenant();
    const sub = this.context.selectedSubscription();
    return tenant && sub ? ['/console', tenant.tenantUuid, sub.subscriptionUuid] : null;
  });

  onToggleSidebar(): void {
    if (this.shell.mobileNavOpen()) {
      this.shell.mobileNavOpen.set(false);
      return;
    }
    this.store.dispatch(UiActions.toggleSidebar());
  }

  /** Collapsed: the context card is icons only, so a click on it expands the sidebar. */
  expand(): void {
    if (this.collapsed()) this.store.dispatch(UiActions.setSidebarCollapsed({ collapsed: false }));
  }

  onLogout(): void {
    this.bridge.logout$().subscribe();
  }
}
